<template>
  <nav aria-label="breadcrumb">
    <v-row class="bg-color-primary2">
      <v-col
        cols="12"
        md="9"
      >
        <v-breadcrumbs class="width-100">
          <v-breadcrumbs-item
            v-for="(breadcrumb, index) in computedBreadcrumbs"
            :key="index"
            :disabled="index === computedBreadcrumbs.length - 1"
            @click="goToBreadcrumb(breadcrumb)"
          >
            <span
              v-if="index !== 0"
              class="mr-2"
              >/</span
            >
            <span class="cursor-pointer">{{ t(breadcrumb.title) }}</span>
          </v-breadcrumbs-item>
        </v-breadcrumbs>
      </v-col>
      <v-col
        cols="12"
        md="3"
        class="text-end pr-8"
        align-self="center"
      >
        <small-button
          text
          brown
          @click="goBack"
        >
          <v-icon icon="mdi-keyboard-backspace" />&nbsp;{{ t('breadcrumbs.back') }}
        </small-button>
      </v-col>
    </v-row>
  </nav>
</template>

<script setup lang="ts">
  import { computed } from 'vue';
  import { BreadcrumbType } from '@/types/BreadcrumbType';
  import router from '@/router';
  import { useI18n } from 'vue-i18n';
  import SmallButton from '@/components/widget/SmallButton.vue';
  import { useRoute } from 'vue-router';
  import { getBreadcrumbsByPath } from '@/utils/breadcrumbs';

  const { t } = useI18n();
  const route = useRoute();

  // Dynamically compute breadcrumbs based on route's meta and params
  const computedBreadcrumbs = computed(() => {
    const breadcrumbs = getBreadcrumbsByPath(route.fullPath);

    if (breadcrumbs.length > 0) {
      return breadcrumbs;
    }
    const params = { ...route.params };
    const paramsKeys = Object.keys(params);

    // Fallback to meta if no matching template found in breadcrumbs.ts
    const metaBreadcrumbs = (route.meta.breadcrumb as BreadcrumbType[]) || [];
    const groupType = route.params.groupType;
    const groupIdentifier = route.params.groupIdentifier;
    const action = route.params.action;

    return metaBreadcrumbs.map((breadcrumb: BreadcrumbType) => {
      let updatedHref = breadcrumb.href;
      let updatedTitle = breadcrumb.title;

      if (updatedHref?.includes(':groupType')) {
        updatedHref = updatedHref.replace(':groupType', groupType as string);
      }
      if (updatedHref?.includes(':groupIdentifier')) {
        updatedHref = updatedHref.replace(':groupIdentifier', groupIdentifier as string);
      }

      if (
        updatedTitle?.includes('groupable.groupTypes') &&
        updatedTitle?.split('.')[2].includes(':')
      ) {
        updatedTitle = `groupable.groupTypes.${groupType}`;
      }
      if (updatedTitle?.includes('groupable.group.breadcrumbTitle')) {
        updatedTitle = `groupable.group.breadcrumbTitle`;
      }

      if (updatedTitle?.includes(':action')) {
        updatedTitle =
          action === 'edit'
            ? `groupable.item.form.edit.${groupType}.title`
            : `groupable.item.form.add.${groupType}.title`;
      }

      for (const key of paramsKeys) {
        updatedHref = updatedHref.replace(`:${key}`, params[key] as string);
      }

      return {
        title: updatedTitle,
        href: updatedHref,
        disabled: false, // Default for fallback
      };
    });
  });

  // Handle click on breadcrumb
  const goToBreadcrumb = (breadcrumb: BreadcrumbType) => {
    if (breadcrumb.href) {
      router.push(breadcrumb.href);
    }
  };

  // Handle 'back' button click
  const goBack = () => {
    if (window.localStorage.getItem('previousRoute')) {
      router.push(window.localStorage.getItem('previousRoute') as string);
    } else {
      router.back();
    }
  };
</script>
